/**
 * src/hooks/useReportReminder.ts
 *
 * 毎日 19:55 JST に「日報提出リマインダー」OS通知を出すフック。
 *
 * 動作条件:
 *   - assistantId が undefined でない（admin には渡さない）
 *   - localStorage に 'mangaos_report_reminder' === 'enabled' が保存済み
 *   - Notification.permission === 'granted'
 *   - 当日の日報未提出（自分の assistant_id で判定）
 *   - JST 19:55〜20:30 の間にページが開いている
 *
 * 制限事項:
 *   - ブラウザ / タブが開いていることが必要（PCスリープ中・ブラウザ終了時は不可）
 *   - Service Worker の showNotification を優先し、使えない場合は new Notification() にフォールバック
 */

import { useEffect } from 'react'
import { api } from '../api/client'

export const REMINDER_STORAGE_KEY = 'mangaos_report_reminder'
const FIRED_DATE_KEY = 'mangaos_report_reminder_fired_date'

// 通知ウィンドウ: JST 19:55〜20:30（遅れてページを開いた場合でも通知できる）
const WINDOW_START_TOTAL = 19 * 60 + 55  // 1195 分
const WINDOW_END_TOTAL   = 20 * 60 + 30  // 1230 分

// バックグラウンドタブでも1分ごとに発火できるよう短いインターバル
const CHECK_INTERVAL_MS = 60 * 1000

/** UTC 時刻から JST の情報を返す */
function getJST(): { date: string; totalMinutes: number } {
  const d = new Date(Date.now() + 9 * 60 * 60 * 1000)
  return {
    date: d.toISOString().slice(0, 10),
    totalMinutes: d.getUTCHours() * 60 + d.getUTCMinutes(),
  }
}

// SW 登録をモジュールスコープでキャッシュ（複数フック呼び出し・再レンダー対応）
let swRegPromise: Promise<ServiceWorkerRegistration | null> | null = null

function ensureSwRegistered(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return Promise.resolve(null)
  if (!swRegPromise) {
    swRegPromise = navigator.serviceWorker
      .register('/mangaos-notification-sw.js', { scope: '/' })
      .catch(() => null)
  }
  return swRegPromise
}

/**
 * OS通知を表示する。
 * Service Worker の registration.showNotification() を優先し、
 * 使えない場合は new Notification() にフォールバックする。
 */
export async function showOsNotification(options: {
  title: string
  body: string
  tag: string
  renotify?: boolean
  requireInteraction?: boolean
}): Promise<void> {
  if (!('Notification' in window) || Notification.permission !== 'granted') return

  const { title, body, tag, renotify = false, requireInteraction = false } = options
  // renotify / requireInteraction は一部の TypeScript DOM 型定義に未収録のため unknown 経由でキャスト
  const notifOptions = {
    body,
    icon: '/favicon.ico',
    tag,
    renotify,
    requireInteraction,
    silent: false,
  } as unknown as NotificationOptions

  try {
    const reg = await ensureSwRegistered()
    if (reg) {
      await reg.showNotification(title, notifOptions)
      return
    }
  } catch {
    // SW が使えない環境はフォールバックへ
  }

  // フォールバック: new Notification()（通知クリック時は現タブをフォーカス）
  const n = new Notification(title, notifOptions)
  n.onclick = () => {
    window.focus()
    window.location.href = '/reports'
  }
}

/**
 * useReportReminder
 * @param assistantId - ログイン中のアシスタントID。undefined の場合は何もしない（admin除外用）
 */
export function useReportReminder(assistantId: string | undefined) {
  useEffect(() => {
    if (!assistantId) return
    if (!('Notification' in window)) return

    // 通知発火前に SW 登録を開始しておく
    ensureSwRegistered()

    const check = async () => {
      // ① 有効化されているか（複数タブ対策: 毎回 localStorage を読む）
      if (localStorage.getItem(REMINDER_STORAGE_KEY) !== 'enabled') return
      // ② 通知許可があるか
      if (Notification.permission !== 'granted') return

      const { date, totalMinutes } = getJST()

      // ③ 19:55〜20:30 の時間帯のみ処理
      if (totalMinutes < WINDOW_START_TOTAL || totalMinutes > WINDOW_END_TOTAL) return

      // ④ 今日すでに通知済みか
      if (localStorage.getItem(FIRED_DATE_KEY) === date) return

      // ⑤ 今日の自分の日報が提出済みか
      try {
        const reports = await api.reports.list({ date, assistant_id: assistantId }) as any[]
        if (reports.length > 0) {
          localStorage.setItem(FIRED_DATE_KEY, date)
          return
        }
      } catch {
        return  // API エラー時は次のインターバルで再試行
      }

      // ⑥ 複数タブが同時に到達した場合の重複防止（最終チェック）
      if (localStorage.getItem(FIRED_DATE_KEY) === date) return
      localStorage.setItem(FIRED_DATE_KEY, date)

      // ⑦ OS通知を発火
      await showOsNotification({
        title: '日報リマインダー',
        body: '本日の日報が未提出です。作業終了前に日報を提出してください。',
        tag: `mangaos-daily-report-reminder-${date}-${assistantId}`,
        renotify: true,
        requireInteraction: true,
      })
    }

    // マウント直後に1回チェック（19:55以降にページを開いた場合に対応）
    check()
    const interval = setInterval(check, CHECK_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [assistantId])
}
